import { useState, useEffect, useCallback } from "react";
import inventoryService from "../../../api/inventoryService";

/**
 * Hook personalizado para manejar la lógica de inventario
 */
export const useInventory = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  // Transformar datos del backend a formato del frontend
  const transformInventoryData = useCallback((stockRecords) => {
    // Agrupar por itemId
    const itemsMap = new Map();

    stockRecords.forEach((record) => {
      // Manejar itemId que puede ser objeto populado o solo ID
      const item = record.itemId;
      const itemId = item?._id || item || null;
      const location = record.locationId;

      if (!itemId || !item || !location) return;

      // Filtrar productos inactivos
      const itemStatus = item.status || "active";
      if (itemStatus === "inactive") return;

      const itemIdStr = itemId.toString();

      // Si no existe el item en el mapa, crearlo
      if (!itemsMap.has(itemIdStr)) {
        itemsMap.set(itemIdStr, {
          _id: item._id || itemId,
          name: item.name || "",
          sku: item.sku || "",
          type: item.type || "",
          category: item.category || "",
          brand: item.brand,
          description: item.description,
          unit: item.unit || "",
          cofeprisCat: item.cofeprisCat,
          status: itemStatus,
          minStock: record.minStock || item.minStock || 0,
          warehouses: [], // Array de almacenes con su stock individual
          technicians: [],
          hazardousStock: 0,
        });
      }

      const product = itemsMap.get(itemIdStr);

      // Agregar stock según el tipo de ubicación
      const quantity = record.quantity || 0;
      
      if (location.type === "main_warehouse") {
        // Agregar al array de almacenes (cada almacén es independiente)
        const locationId = location._id?.toString() || location._id;
        const existingWarehouse = product.warehouses.find(
          (w) => (w.locationId?.toString() || w.locationId) === locationId
        );
        
        if (existingWarehouse) {
          existingWarehouse.stock += quantity;
        } else {
          product.warehouses.push({
            locationId: locationId,
            locationName: location.name || "Almacén",
            stock: quantity,
            minStock: record.minStock || 0,
          });
        }
        
        // Actualizar minStock si existe en el record
        if (existingWarehouse && record.minStock !== undefined) {
          existingWarehouse.minStock = record.minStock;
        }
      } else if (location.type === "technician") {
        // Buscar si ya existe el técnico en el array
        const techId = location.technicianId?._id || location.technicianId;
        const techIdStr = techId?.toString() || techId;
        const locId = location._id?.toString() || location._id;
        
        const existingTech = product.technicians.find(
          (t) => (t.technicianId?.toString() || t.technicianId) === techIdStr
        );
        
        if (existingTech) {
          existingTech.stock += quantity;
        } else {
          product.technicians.push({
            technicianId: techIdStr,
            locationId: locId, // Agregar locationId para poder actualizar minStock
            technicianName: location.technicianName || "",
            stock: quantity,
            minStock: record.minStock || 0,
          });
        }
        
        // Actualizar minStock si existe en el record
        if (existingTech && record.minStock !== undefined) {
          existingTech.minStock = record.minStock;
        }
      } else if (location.type === "dangerous_goods") {
        product.hazardousStock += quantity;
      }
    });

    return Array.from(itemsMap.values());
  }, []);

  // Cargar productos
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await inventoryService.getInventory();

      // Transformar los datos al formato esperado por el frontend
      const transformedProducts = transformInventoryData(res.data);

      setProducts(transformedProducts);
      return transformedProducts;
    } catch (err) {
      console.error("Error cargando inventario:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transformInventoryData]);

  // Cargar productos al montar
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Crear producto
  const createProduct = useCallback(
    async (data) => {
      try {
        await inventoryService.create(data);
        await loadProducts();
        return true;
      } catch (err) {
        console.error("Error creando producto:", err);
        throw err;
      }
    },
    [loadProducts]
  );

  // Actualizar producto
  const updateProduct = useCallback(
    async (id, data) => {
      try {
        await inventoryService.update(id, data);
        await loadProducts();
        return true;
      } catch (err) {
        console.error("Error actualizando producto:", err);
        throw err;
      }
    },
    [loadProducts]
  );

  // Eliminar producto
  const deleteProduct = useCallback(
    async (id) => {
      try {
        await inventoryService.delete(id);
        await loadProducts();
        setSelected(null);
        return true;
      } catch (err) {
        console.error("Error eliminando producto:", err);
        throw err;
      }
    },
    [loadProducts]
  );

  // Transferir stock
  const transferStock = useCallback(
    async (transferData) => {
      try {
        await inventoryService.transfer(transferData);
        await loadProducts();
        return true;
      } catch (err) {
        console.error("Error en transferencia:", err);
        throw err;
      }
    },
    [loadProducts]
  );

  // Consumir stock
  const consumeStock = useCallback(
    async (consumeData) => {
      try {
        await inventoryService.consume(consumeData);
        await loadProducts();
        return true;
      } catch (err) {
        console.error("Error en consumo:", err);
        throw err;
      }
    },
    [loadProducts]
  );

  // Recibir stock
  const receiveStock = useCallback(
    async (receiveData) => {
      try {
        await inventoryService.receive(receiveData);
        await loadProducts();
        return true;
      } catch (err) {
        console.error("Error en recepción:", err);
        throw err;
      }
    },
    [loadProducts]
  );

  return {
    products,
    selected,
    setSelected,
    loading,
    loadProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    transferStock,
    consumeStock,
    receiveStock,
  };
};

